import { getFlattenedNodes, SummaryType } from "@ls/grouping-service";
import { createSelector } from "reselect";
import { uniqueByKey, memoize } from "@ls/js-utils";
import CalcType from "state/Constants/CalcType";
import ScaleType from "state/Constants/ScaleType";
import SelectionState from "state/selections/SelectionState";
import {
  selectSelectedAccountEntityId,
  selectSelectedBenchmarkEntityId,
  selectSelectedAccountEntity,
} from "state/selections/selectors";
import {
  getEntitySummariesObject,
  getEntitySummariesObjectForBench,
} from "state/util";
import scope from "./scope";

export const selectData = (summaryType) => (state) =>
  state[scope]["data"][summaryType];
export const selectRatioData = (summaryType) => (state) =>
  state[scope]["ratioData"][summaryType];
export const selectSbsData = (summaryType) => (state) =>
  state[scope]["sbsData"][summaryType];
export const selectFetchingData = (summaryType) => (state) =>
  state[scope]["fetchingData"][summaryType];
export const selectFetchingRatioData = (summaryType) => (state) =>
  state[scope]["fetchingRatioData"][summaryType];
export const selectFetchingSbsData = (summaryType) => (state) =>
  state[scope]["fetchingSbsData"][summaryType];
export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return (state) => state[scope]["definitions"][selectionState];
  }

  return createSelector(
    (state) => state[scope]["definitions"][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(
        ({ complete }) => complete,
      );
      return completeDefinitions.filter(
        uniqueByKey(completeDefinitions, "key"),
      );
    },
  );
});

export const selectRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectData(SummaryType.ABSOLUTE),
  selectData(SummaryType.RELATIVE),
  selectRatioData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (
    definitions,
    absData,
    relData,
    ratioData,
    accEntityId,
    bmkEntityId,
    benchId,
  ) => {
    const absoluteNodes = getFlattenedNodes(absData?.root) ?? [];
    const relativeNodes = getFlattenedNodes(relData?.root) ?? [];
    const ratioNodes = getFlattenedNodes(ratioData?.root) ?? [];

    const rowMap = [...absoluteNodes, ...relativeNodes].reduce(
      (rowMap, node) => {
        const { pathString } = node;
        rowMap[pathString] = rowMap[pathString] ?? { pathString };
        const row = rowMap[pathString];
        const mappedDefinitions = row.__mappedDefinitions ?? {};
        definitions
          .filter(({ key }) => !mappedDefinitions[key])
          .filter((definition) => definition.scaleType !== ScaleType.Ratio)
          .forEach((definition) => {
            const [textValue, numericValue] = findRatingValue(
              node,
              definition,
              accEntityId,
              bmkEntityId,
              benchId?.benchmarkId,
            );
            row[`${definition.key}-text`] = textValue;
            row[`${definition.key}-numeric`] = numericValue;
            mappedDefinitions[definition.key] = true;
          });
        row.__mappedDefinitions = mappedDefinitions;
        return rowMap;
      },
      {},
    );

    ratioNodes.forEach((node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      const mappedDefinitions = row.__mappedDefinitions ?? {};
      definitions
        .filter(({ key }) => !mappedDefinitions[key])
        .filter((definition) => definition.scaleType === ScaleType.Ratio)
        .forEach((definition) => {
          const [textValue, numericValue] = findRatingValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId,
          );
          row[`${definition.key}-text`] = textValue;
          row[`${definition.key}-numeric`] = numericValue;
          mappedDefinitions[definition.key] = true;
        });
      row.__mappedDefinitions = mappedDefinitions;
    });

    return rowMap;
  },
);

export const selectSbsRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectSbsData(SummaryType.ABSOLUTE),
  selectSbsData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, absSbsData, relSbsData, accEntityId, bmkEntityId, benchId) => {
    const absoluteSbsNodes = getFlattenedNodes(absSbsData?.root) ?? [];
    const relativeSbsNodes = getFlattenedNodes(relSbsData?.root) ?? [];
    return [...absoluteSbsNodes, ...relativeSbsNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      const mappedDefinitions = row.__mappedDefinitions ?? {};
      definitions
        .filter(({ key }) => !mappedDefinitions[key])
        .forEach((definition) => {
          const [textValue, numericValue] = findRatingValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId,
          );
          row[`${definition.key}-text - scenario`] = textValue;
          row[`${definition.key}-numeric - scenario`] = numericValue;
          mappedDefinitions[definition.key] = true;
        });
      row.__mappedDefinitions = mappedDefinitions;
      return rowMap;
    }, {});
  },
);

const findRatingValue = (
  node,
  definition,
  accEntityId,
  bmkEntityId,
  benchId,
) => {
  const { ratingId, analytic, calcType, scaleType } = definition;
  const normalizedScaleType = scaleType === ScaleType[50] ? "50" : scaleType;
  const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
  const benchValue = calcType === CalcType.Benchmark ? 0 : benchId;
  const isRelative = calcType === CalcType.Relative;
  let entitySummariesObject;
  if (benchValue !== null && benchValue !== undefined) {
    entitySummariesObject = getEntitySummariesObjectForBench(
      node,
      entityId,
      benchValue,
      isRelative,
    );
  } else {
    entitySummariesObject =
      getEntitySummariesObject(node, entityId, isRelative) ?? {};
  }

  const result = [];

  // text result should prefer the rating label for the selected rating,
  // but fall back to the analytic payload when the label is missing.
  let analyticValueMap;
  let percentileData = null;
  switch (normalizedScaleType) {
    case ScaleType.Contribution:
    case ScaleType.Ratio:
      analyticValueMap = entitySummariesObject?.["values"] ?? [];
      break;
    case ScaleType.WeightedAverage:
      analyticValueMap = entitySummariesObject?.["rescaledValues"] ?? [];
      break;
    case "25":
    case "50":
    case "75": {
      const percentiles = entitySummariesObject?.["percentiles"] ?? [];
      const percentileValue =
        normalizedScaleType === "50" ? 50 : parseInt(normalizedScaleType, 10);
      percentileData = percentiles.find(
        (p) => p.percentile === percentileValue,
      );
      analyticValueMap = percentileData ?? {};
      break;
    }
    default:
      analyticValueMap = {};
  }

  const numericValue = analyticValueMap?.[analytic] ?? null;
  const textValue =
    normalizedScaleType === "25" ||
    normalizedScaleType === "50" ||
    normalizedScaleType === "75"
      ? (percentileData?.qualityRatings?.find(
          (qRating) => qRating.type === ratingId,
        )?.rating ?? null)
      : (entitySummariesObject?.qualityRatings?.find(
          (qRating) => qRating.type === ratingId,
        )?.rating ?? numericValue);

  result[0] = textValue;
  result[1] = numericValue;

  return result;
};
